import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, MAINTENANCE_DETECTED_EVENT, type MaintenanceState } from "../lib/api";

const CHECK_INTERVAL_MS = 60_000;
const PUBLIC_PATHS = new Set(["/", "/landing", "/sales-support", "/president-consult", "/knowledge-use", "/login", "/email-otp/email", "/email-otp/verify", "/line-login", "/support-inquiry"]);
const JST_TIME_ZONE = "Asia/Tokyo";

function formatJstDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parts = new Intl.DateTimeFormat("ja-JP", {
    timeZone: JST_TIME_ZONE,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}年${part("month")}月${part("day")}日 ${part("hour")}:${part("minute")}（日本時間）`;
}

function splitMessageIntoSentences(message: string): string[] {
  return message.match(/[^。]+。|[^。]+$/g) ?? [message];
}

export function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<MaintenanceState | null>(null);
  const isPublicLanding = PUBLIC_PATHS.has(location.pathname);

  useEffect(() => {
    if (isPublicLanding) return undefined;
    let active = true;
    const refresh = async () => {
      try {
        const account = await api.getMe();
        if (active) setState(account.maintenance ?? { enabled: false, mode: "normal", title: null, message: null, estimatedEndAt: null, nextUpdateAt: null, contactText: null, contactUrl: null, revision: 0, accessAllowed: false });
      } catch {
        // A protected API request will independently switch the user to the maintenance UI.
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), CHECK_INTERVAL_MS);
    const onMaintenanceDetected = () => void refresh();
    window.addEventListener(MAINTENANCE_DETECTED_EVENT, onMaintenanceDetected);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(MAINTENANCE_DETECTED_EVENT, onMaintenanceDetected);
    };
  }, [isPublicLanding]);

  if (!isPublicLanding && state?.enabled && !state.accessAllowed) {
    const isExpired = Boolean(state.estimatedEndAt) && new Date(state.estimatedEndAt as string).getTime() < Date.now();
    const message = state.message || "システム更新のため、一時的にご利用いただけません。";
    const scheduleItems = [
      ...(isExpired
        ? [{ label: "現在の状況", value: "予定を超過しており、状況を確認中です" }]
        : state.estimatedEndAt
          ? [{ label: "終了予定", value: formatJstDateTime(state.estimatedEndAt) }]
          : []),
      ...(state.nextUpdateAt ? [{ label: "次回状況更新", value: formatJstDateTime(state.nextUpdateAt) }] : []),
    ];

    return (
      <main className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-gradient-to-br from-[#FFF9F4] via-[#FFFDFC] to-[#F5E8DD] px-5 py-10 text-[#4A3E39]">
        <div aria-hidden="true" className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-[#F1D8C7]/60 blur-3xl" />
        <div aria-hidden="true" className="absolute -bottom-24 -right-16 h-80 w-80 rounded-full bg-[#EBD7C8]/70 blur-3xl" />
        <section className="relative w-full max-w-xl rounded-[2rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_70px_rgba(96,69,55,0.16)] backdrop-blur sm:p-10">
          <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-[#FDF7F2] shadow-lg shadow-[#7C5A4E]/20">
            <img
              src="/img/lp-hero-mei.jpg"
              alt="メイ"
              className="h-full w-full object-cover object-[48%_30%]"
            />
          </div>
          <p className="mt-5 text-center text-sm font-bold tracking-[0.16em] text-[#A67C52]">WIZMATE からのお知らせ</p>
          <h1 className="mt-3 text-center text-2xl font-black tracking-tight text-[#4A3E39] sm:text-3xl">{state.title || "メンテナンス中です"}</h1>
          <p className="mx-auto mt-4 max-w-md text-center text-sm font-bold leading-7 text-[#6B5851]">ご不便をおかけして申し訳ありません。</p>

          <div className="mt-7 rounded-[1.5rem] bg-[#FDF7F2] px-5 py-5 text-sm leading-7 text-[#5D4D46] sm:px-6">
            {splitMessageIntoSentences(message).map((sentence, index) => (
              <p key={`${sentence}-${index}`} className={index === 0 ? "" : "mt-3"}>{sentence}</p>
            ))}
          </div>

          {scheduleItems.length > 0 && (
            <dl className="mt-5 divide-y divide-[#EADBC8] overflow-hidden rounded-[1.25rem] border border-[#EADBC8] bg-white">
              {scheduleItems.map((item) => (
                <div key={item.label} className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                  <dt className="text-xs font-black tracking-wide text-[#A67C52]">{item.label}</dt>
                  <dd className="text-sm font-bold text-[#4A3E39]">{item.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {state.contactUrl && (
            <div className="mt-6 rounded-[1.25rem] border border-[#EADBC8] bg-[#FFFDFC] px-5 py-4 text-center">
              <p className="text-sm font-bold text-[#5D4D46]">{state.contactText || "お問い合わせ"}</p>
              <a href={state.contactUrl} className="mt-2 block break-all text-sm font-black text-[#A35D40] underline decoration-[#D9AD91] underline-offset-4 hover:text-[#7C5A4E]">
                {state.contactUrl}
              </a>
            </div>
          )}
        </section>
      </main>
    );
  }
  return <>{state?.enabled && state.accessAllowed && <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 10000, padding: 8, textAlign: "center", color: "#fff", background: "#b45309" }}>メンテナンス中です（許可ユーザーとして利用中）</div>}{children}</>;
}
